import React, { useEffect, useState } from 'react';
import { Modal, Form, Input, DatePicker, Select, Checkbox, Col, Row, message } from 'antd';
import moment from 'moment';
import { EditEstateOwnerPayload, Estate, EstateOwner } from '../../types';
import { editEstateOwner } from './estateService';

const { Option } = Select;

interface EditOwnerModalProps {
  open: boolean;
  estateId: string;
  owner?: EstateOwner | null;
  onCancel: () => void;
  onSubmit: (estate: Estate) => void;
}

/**
 * Edits the estate owner on its own.
 *
 * The owner is the legal person the estate belongs to, which is not always the
 * person who died: that is the decedent, and it has its own form.
 */
const EditOwnerModal: React.FC<EditOwnerModalProps> = ({
  open,
  estateId,
  owner,
  onCancel,
  onSubmit,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;

    form.setFieldsValue({
      prefix: owner?.prefix || '',
      firstName: owner?.firstName || '',
      middleName: owner?.middleName || '',
      lastName: owner?.lastName || '',
      suffix: owner?.suffix || '',
      sex: owner?.sex || undefined,
      dob: owner?.dob ? moment(owner.dob) : null,
      isDeceased: owner?.deceased || false,
    });
  }, [open, owner, form]);

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      const payload: EditEstateOwnerPayload = {
        prefix: values.prefix || '',
        firstName: values.firstName,
        middleName: values.middleName || '',
        lastName: values.lastName,
        suffix: values.suffix || '',
        sex: values.sex || '',
        dob: values.dob ? values.dob.format('YYYY-MM-DD') : undefined,
        isDeceased: values.isDeceased || false,
      };

      const estate = await editEstateOwner(estateId, payload);
      onSubmit(estate);
      message.success('Owner updated successfully');
      onCancel();
    } catch (error: any) {
      if (error?.errorFields) return; // validation, already shown on the fields
      console.error(error);
      message.error(error.message || 'Failed to update owner.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title="Edit Owner"
      open={open}
      onOk={handleOk}
      onCancel={onCancel}
      okText="Save"
      cancelText="Cancel"
      confirmLoading={loading}
    >
      <Form form={form} layout="vertical">
        <Row gutter={16}>
          <Col span={6}>
            <Form.Item label="Prefix" name="prefix">
              <Input placeholder="e.g., Mr., Ms." />
            </Form.Item>
          </Col>
          <Col span={9}>
            <Form.Item
              label="First Name"
              name="firstName"
              rules={[{ required: true, message: 'Please enter the first name!' }]}
            >
              <Input placeholder="Enter first name" />
            </Form.Item>
          </Col>
          <Col span={9}>
            <Form.Item label="Middle Name" name="middleName">
              <Input placeholder="Enter middle name (optional)" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label="Last Name"
              name="lastName"
              rules={[{ required: true, message: 'Please enter the last name!' }]}
            >
              <Input placeholder="Enter last name" />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item label="Suffix" name="suffix">
              <Input placeholder="e.g., Jr., Sr." />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item label="Sex" name="sex">
              <Select placeholder="Select sex" allowClear>
                <Option value="male">Male</Option>
                <Option value="female">Female</Option>
                <Option value="other">Other</Option>
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Form.Item label="Date of Birth" name="dob">
          <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" placeholder="Select date of birth" />
        </Form.Item>

        <Form.Item name="isDeceased" valuePropName="checked">
          <Checkbox>Is Deceased</Checkbox>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default EditOwnerModal;
